import styled from '@emotion/styled';
import { ComponentProps } from 'react';
import { TextField as TextFieldMui } from '@mui/material';
import { Colors } from '../../styles/variables/colors';
import { Variables } from '../../styles/variables/variables';
import { Breakpoints } from '../../styles/variables/breakpoints';

const { darkGray, inputBorder, lightBlack, red, blue } = Colors;
const { montserrat, assistant } = Variables;
const { sm } = Breakpoints;

type Props = {
    justifyItems?: boolean;
};
type SwitchCaseProps = {
    active?: boolean;
};

export const AssessmentLink = styled.div`
    margin-top: 7px;
    margin-bottom: 16px;
    button {
        color: ${blue};
        text-decoration: underline;
    }
`;

const AssessmentQuestionStyled = styled.div`
    margin-top: 7px;
    margin-bottom: 0px;
    display: flex;
    flex-direction: column;
`;

export const AssessmentQuestion = (props: ComponentProps<typeof AssessmentQuestionStyled>) => (
    <AssessmentQuestionStyled data-form-question="" {...props} />
);

export const CardContent = styled.div`
    display: flex;
    margin-top: 20px;
    gap: 10px;

    @media screen and (max-width: ${sm}) {
        flex-direction: column;
    }
`;

export const ContentDivider = styled.div`
    background: ${inputBorder};
    width: 100%;
    height: 1px;
    margin: 30px 10px 15px 0px;
`;

export const DeleteButton = styled.div`
    button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 40px;
        height: 40px;
        border-radius: 20px;
        border: 1px solid ${inputBorder};
        color: ${lightBlack};
        cursor: pointer;
    }
`;

export const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
`;

const FormContainerStyled = styled.div`
    display: grid;
    grid-row-gap: 15px;

    @media screen and (max-width: 1200px) {
        grid-row-gap: 0px;
    }
`;

export const FormContainer = (props: ComponentProps<typeof FormContainerStyled>) => (
    <FormContainerStyled data-form-grid="container" {...props} />
);

const EntryCardStyled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    padding: 16px;
    margin: 4px 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #ffffff;
    box-sizing: border-box;
`;

export const EntryCard = (props: ComponentProps<typeof EntryCardStyled>) => (
    <EntryCardStyled data-form-entry="card" {...props} />
);

export const FormNote = styled.p`
    margin: 0;
    color: var(--profile-primary, #6025d8);
    font-size: 0.875rem;
    line-height: 1.4;
`;

export const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 22px 0px 18px 0px;
`;

export const FooterButton = styled.button`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 14px;
    margin-left: 1rem;
    gap: 8px;
    background: #ffffff;
    border: 1px solid var(--profile-primary, #6025d8);
    border-radius: 8px;
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    max-height: 40px;
    color: var(--profile-primary, #6025d8);
    cursor: pointer;

    &:hover:not(:disabled) {
        background: var(--profile-primary-muted, rgba(96, 37, 216, 0.1));
    }

    &:focus-visible {
        outline: 2px solid var(--profile-primary, #6025d8);
        outline-offset: 2px;
    }
`;

export const SubmitFooterButton = styled.button`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 14px;
    gap: 8px;
    background: var(--profile-primary, #6025d8);
    border: none;
    border-radius: 8px;
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: var(--profile-on-primary, #ffffff);
    cursor: pointer;
`;

const FooterTitleStyled = styled.div`
    font-family: ${montserrat};
    font-style: normal;
    font-weight: 600;
    font-size: 24px;
    line-height: 30px;
    text-transform: capitalize;
    color: ${darkGray};
`;

export const FooterTitle = (props: ComponentProps<typeof FooterTitleStyled>) => (
    <FooterTitleStyled data-form-footer-title="" {...props} />
);

const GridSectionStyled = styled.div<Props>`
    margin: 15px 0px;
    width: 100%;
    display: grid;
    grid-template-columns: ${(props) => (props.justifyItems ? '1fr' : '1fr 1fr')};
    grid-column-gap: 15px;
    grid-row-gap: 15px;

    @media screen and (max-width: 1200px) {
        grid-template-columns: 1fr;
    }

    @media screen and (max-width: 500px) {
        justify-items: ${(props) => (props.justifyItems ? 'center' : '')};
    }
`;

export const GridSection = (props: ComponentProps<typeof GridSectionStyled>) => (
    <GridSectionStyled data-form-grid="section" {...props} />
);

const SliderGridSectionStyled = styled.div<Props>`
    margin: 15px 0px;
    width: 100%;
    display: grid;
    grid-template-columns: ${(props) => (props.justifyItems ? '1fr' : '1fr 1fr')};
    grid-column-gap: 30px;
    grid-row-gap: 15px;

    @media screen and (max-width: ${sm}) {
        grid-template-columns: 1fr;
    }

    @media screen and (max-width: 500px) {
        justify-items: ${(props) => (props.justifyItems ? 'center' : '')};
    }
`;

export const SliderGridSection = (props: ComponentProps<typeof SliderGridSectionStyled>) => (
    <SliderGridSectionStyled data-form-grid="section" {...props} />
);

const LanguageTitleStyled = styled.p`
    font-weight: bolder;
    margin: 0;
    font-size: 18px;
    display: flex;
    justify-content: space-between;
    font-family: ${montserrat};
    font-style: normal;
    line-height: 20px;
    color: ${darkGray};
`;

export const LanguageTitle = (props: ComponentProps<typeof LanguageTitleStyled>) => (
    <LanguageTitleStyled data-form-language-title="" {...props} />
);

export const LanguageSection = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 7px;
    margin-bottom: 0px;
`;

export const ListItem = styled.li`
    margin-left: 30px;
    list-style-type: circle;
`;

export const PasswordGridSection = styled.div`
    margin: 15px 0px;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 15px;
    grid-row-gap: 15px;
    margin: 0 !important;
    grid-row-gap: 0 !important;

    @media screen and (max-width: ${sm}) {
        grid-template-columns: 1fr;
        grid-row-gap: 15px !important;
    }
`;

export const SpouseButton = styled.button`
    width: 160px;
    height: 50px;
    border: 1px solid var(--profile-primary, #6025d8);
    background: var(--profile-primary, #6025d8);
    color: var(--profile-on-primary, #ffffff);
    border-radius: 8px;
    margin: auto;
    margin-top: 1.5rem;
    cursor: pointer;

    &:hover {
        background: var(--profile-primary-dark, #4c1dab);
    }

    &:focus-visible {
        outline: 2px solid var(--profile-primary, #6025d8);
        outline-offset: 2px;
    }
`;

export const Text = styled.p`
    font-family: ${assistant};
    font-style: normal;
    font-weight: 400;
    font-size: 15px;
    line-height: 20px;
    color: ${darkGray};
    margin-top: 0.75rem;
`;

const SubTitleStyled = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: ${montserrat};
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    color: ${darkGray};
`;

export const SubTitle = (props: ComponentProps<typeof SubTitleStyled>) => (
    <SubTitleStyled data-form-subtitle="" {...props} />
);

export const TextField = styled(TextFieldMui)`
    input,
    label {
        font-style: normal;
        font-weight: 400;
        font-size: 15px;
        line-height: 20px;
    }
`;

const TitleStyled = styled.p`
    font-family: ${montserrat};
    font-style: normal;
    font-weight: 600;
    font-size: 28px;
    line-height: 34px;
    text-transform: capitalize;
    color: ${darkGray};
    margin-bottom: 2rem;
    letter-spacing: -0.3px;

    @media screen and (max-width: 1200px) {
        margin-bottom: 1rem;
    }
`;

// Render with a stable `data-form-section-title` attribute so container
// styles (e.g. the ClientProfile edit pane) can hide the form's built-in
// title without relying on Emotion's babel component-selector feature,
// which isn't enabled in this project.
export const Title = (props: ComponentProps<typeof TitleStyled>) => (
    <TitleStyled data-form-section-title="" {...props} />
);

export const ToggleButtonDiv = styled.div`
    margin-left: 9px;
    display: flex;
    flex-direction: row;
    align-items: center;
`;

export const ToolTipHeading = styled.p`
    font-size: 18px;
    font-weight: 600;
`;
export const ToolTipItem = styled.p`
    font-size: 16px;
    margin-left: 20px;
`;

export const ValidationMsg = styled.span`
    color: ${red};
`;
export const partnerSwitchButton = styled.button<SwitchCaseProps>`
    height: 34px;
    min-width: 96px;
    padding: 0 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: ${(props) => (props.active ? 'none' : '1px solid var(--profile-primary, #6025d8)')};
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.01em;
    white-space: nowrap;
    cursor: ${(props) => (props.active ? 'default' : 'pointer')};
    background: ${(props) => (props.active ? 'var(--profile-primary, #6025d8)' : '#ffffff')};
    color: ${(props) =>
        props.active ? 'var(--profile-on-primary, #ffffff)' : 'var(--profile-primary, #6025d8)'};

    &:hover:not(:disabled) {
        ${(props) =>
            props.active ? '' : `background: var(--profile-primary-muted, rgba(96, 37, 216, 0.1));`}
    }

    &:focus-visible {
        outline: 2px solid var(--profile-primary, #6025d8);
        outline-offset: 2px;
    }
`;

export const LoadingContainer = styled.div`
    margin-top: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const TitleContainer = styled.div`
    display: flex;
    @media screen and (max-width: 1140px) {
        flex-direction: column;
    }
`;

export const ButtonContainer = styled.div`
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;

    @media screen and (max-width: 1140px) {
        margin-left: 0px;
        margin-top: 12px;
    }
`;

export const TitleCVContainer = styled.div`
    display: flex;
    justify-content: space-between;
`;

export const CVInfoContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
    margin-bottom: 1rem;
`;

/* Auto-fill button styled to match the modal-aligned UI (rounded purple
 * pill with a soft purple shadow). The $loading + $cycle props keep the
 * existing "color cycle" effect that signals the AI is processing the
 * uploaded document — we just translate the legacy purple/blue swap
 * onto the new gradient look so the visual rhythm stays the same. */
export const AutoFillButton = styled.button<{ $loading?: boolean; $cycle?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px 16px;
    border: none;
    border-radius: 8px;
    color: var(--profile-on-primary, #ffffff);
    font-family: ${assistant};
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.01em;
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;
    background: ${({ $loading, $cycle }) =>
        $loading && !$cycle ? '#0095a9' : 'var(--profile-primary, #6025d8)'};

    &:hover:not(:disabled) {
        background: ${({ $loading, $cycle }) =>
            $loading && !$cycle ? '#007a8a' : 'var(--profile-primary-dark, #4c1dab)'};
    }

    &:focus-visible {
        outline: 2px solid var(--profile-primary, #6025d8);
        outline-offset: 2px;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.95;
    }

    svg path {
        fill: var(--profile-on-primary, #ffffff);
    }
`;
